/**
 * OS REPASSES, só leitura (fase 6 do espelho, 19/10/2026), em duas telas.
 *
 * - **Da congregação:** a regra em vigor no mês, os repasses do mês e o histórico dos doze meses
 *   anteriores.
 * - **Do Campo:** todos os repasses do mês, a regra em vigor de cada congregação e o resumo nas
 *   três direções, separado pela situação.
 *
 * A tabela é a da tela "Repasses" do notebook (`Repasses.tsx`): DE / PARA, MOTIVO, CALCULADO, PAGO
 * e SITUAÇÃO, com "ainda não" no pago que não houve, o motivo quando o pago difere do calculado, e
 * a linha do último estorno do pagamento. Os botões de aprovar, pagar e estornar ficam no notebook.
 */
import type { JSX } from 'react'
import {
  alcanca, servidor, type Eu, type LinhaDeRepasseNaTela, type PorSituacao, type RegraEmVigor,
  type RepassesDaCongregacao as Congregacao, type RepassesDoCampo as DoCampo
} from '../api'
import { AtualizadoAs } from '../componentes/AtualizadoAs'
import { BarraDeCima } from '../componentes/BarraDeCima'
import { NavegarMes, useTelaDoMes } from '../componentes/TelaDoMes'
import { competenciaPorExtenso, dataDoNotebook, emReais } from '../dinheiro'
import {
  ehParaImprimir, enderecoDaCongregacao, enderecoDaTelaDaCongregacao, enderecoDaTelaDoCampo,
  enderecoDoCampo, irPara
} from '../rotas'

/** "15% sobre dízimos e votos · regra geral do Campo, desde 01/01/2026". */
function aRegra(regra: RegraEmVigor): string {
  const desde = regra.vigencia_inicio === null ? '' : `desde ${dataDoNotebook(regra.vigencia_inicio)}`
  const ate = regra.vigencia_fim === null ? '' : ` até ${dataDoNotebook(regra.vigencia_fim)}`
  const deQuem = regra.geral ? 'regra geral do Campo' : 'regra desta congregação'
  return regra.vigencia_inicio === null ? regra.resumo : `${regra.resumo} · ${deQuem}, ${desde}${ate}`
}

function TabelaDeRepasses({ linhas, vazio }: { linhas: LinhaDeRepasseNaTela[]; vazio: string }): JSX.Element {
  return (
    <div className="rola-a-tabela">
      <p className="rola-para-o-lado so-tela">Arraste a folha para o lado para ver todas as colunas.</p>
      <table className="grade tabela-de-repasses">
        <colgroup>
          <col style={{ width: '22%' }} /><col style={{ width: '17%' }} />
          <col style={{ width: '15%' }} /><col style={{ width: '16%' }} /><col style={{ width: '30%' }} />
        </colgroup>
        <thead>
          <tr>
            <th>DE / PARA</th><th>MOTIVO</th><th className="valor">CALCULADO</th><th className="valor">PAGO</th>
            <th>SITUAÇÃO</th>
          </tr>
        </thead>
        <tbody>
          {linhas.map((p) => (
            <tr key={p.id}>
              <td className="impresso">
                {p.origem} → {p.destino}
                {p.avulso && <span className="etiqueta">avulso</span>}
                {p.departamento_destino && <span className="etiqueta">{p.departamento_destino}</span>}
              </td>
              <td className="explicacao">{p.motivo}</td>
              <td className="valor manuscrito">{emReais(p.valor_calculado)}</td>
              <td className="valor manuscrito">
                {p.valor_pago === null ? 'ainda não' : emReais(p.valor_pago)}
                {p.motivo_valor_pago && <span className="explicacao motivo-do-valor-pago"> {p.motivo_valor_pago}</span>}
              </td>
              <td>
                {p.situacao}
                {p.aprovado_por && <span className="explicacao"> por {p.aprovado_por}</span>}
                {p.estorno_do_pagamento && (
                  <span className="explicacao estorno-do-pagamento">
                    Pagamento estornado em {dataDoNotebook(p.estorno_do_pagamento.em)}
                    {p.estorno_do_pagamento.por !== null && <> por {p.estorno_do_pagamento.por}</>}
                    {p.estorno_do_pagamento.motivo ? <>: {p.estorno_do_pagamento.motivo}</> : '.'}
                  </span>
                )}
              </td>
            </tr>
          ))}
          {linhas.length === 0 && (
            <tr><td colSpan={5} className="lapis">{vazio}</td></tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

export function RepassesDaCongregacao({
  eu, id, competenciaDoEndereco, aoSair
}: {
  eu: Eu
  id: string
  competenciaDoEndereco: string | null
  aoSair: () => void
}): JSX.Element {
  const paraImprimir = ehParaImprimir()
  const { dados, erro, atualizacao } = useTelaDoMes<Congregacao>(
    `${id}|${competenciaDoEndereco ?? ''}`,
    () => servidor.repassesDaCongregacao(id, competenciaDoEndereco),
    aoSair,
    paraImprimir
  )
  const mes = dados?.competencia ?? competenciaDoEndereco ?? null
  const andar = (para: string): void => irPara(enderecoDaTelaDaCongregacao('repasses', id, para))

  return (
    <>
      {!paraImprimir && (
        <BarraDeCima eu={eu} aoSair={aoSair} voltarPara={{ endereco: enderecoDaCongregacao(id, mes), texto: 'Folhas' }} />
      )}
      <main className="folha folha-lancamento tela-dos-repasses" data-pronto={dados !== null ? 'sim' : erro !== null ? 'erro' : 'nao'}>
        {erro !== null && <p className="recado" role="alert">{erro}</p>}
        {dados === null && erro === null && <p className="recado aguarde">Carregando...</p>}

        {dados !== null && (
          <>
            {!paraImprimir && <AtualizadoAs atualizacao={atualizacao} />}
            {mes !== null && !paraImprimir && <NavegarMes mes={mes} andar={andar} />}

            <h1>REPASSES – {dados.competencia_por_extenso.toUpperCase()}</h1>
            <p className="explicacao centro">{dados.congregacao.nome}</p>

            <p className="regra-em-vigor">
              <strong>Regra em vigor em {dados.competencia_por_extenso}:</strong> {aRegra(dados.regra_em_vigor)}
            </p>

            <TabelaDeRepasses linhas={dados.linhas} vazio={`Nenhum repasse em ${dados.competencia_por_extenso}.`} />

            <h2>Os meses anteriores</h2>
            {dados.historico.length === 0 ? (
              <p className="explicacao">Nenhum repasse nos doze meses antes de {dados.competencia_por_extenso}.</p>
            ) : (
              <div className="rola-a-tabela">
              <table className="grade historico-dos-repasses">
                <colgroup>
                  <col style={{ width: '34%' }} /><col style={{ width: '22%' }} /><col style={{ width: '22%' }} /><col style={{ width: '22%' }} />
                </colgroup>
                <thead>
                  <tr><th>MÊS</th><th className="valor">CALCULADO</th><th className="valor">PAGO</th><th className="valor">FALTA</th></tr>
                </thead>
                <tbody>
                  {dados.historico.map((h) => (
                    <tr key={h.competencia}>
                      <td>
                        <button className="como-link" onClick={() => andar(h.competencia)}>{competenciaPorExtenso(h.competencia)}</button>
                      </td>
                      <td className="valor manuscrito">{emReais(h.calculado)}</td>
                      <td className="valor manuscrito">{emReais(h.pago)}</td>
                      <td className="valor manuscrito">
                        {h.aguardando + h.a_pagar === 0 ? '' : emReais(h.aguardando + h.a_pagar)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
            <p className="explicacao">
              FALTA é o que ainda aguarda aprovação ou foi aprovado e não pago. O estornado não entra.
            </p>
          </>
        )}
      </main>
    </>
  )
}

function LinhaDoResumo({ rotulo, r }: { rotulo: string; r: PorSituacao }): JSX.Element {
  return (
    <tr>
      <td>{rotulo}</td>
      <td className="valor manuscrito">{emReais(r.aguardando)}</td>
      <td className="valor manuscrito">{emReais(r.aprovado)}</td>
      <td className="valor manuscrito">{emReais(r.pago)}</td>
    </tr>
  )
}

export function RepassesDoCampo({
  eu, competenciaDoEndereco, aoSair
}: {
  eu: Eu
  competenciaDoEndereco: string | null
  aoSair: () => void
}): JSX.Element {
  const paraImprimir = ehParaImprimir()
  const { dados, erro, atualizacao } = useTelaDoMes<DoCampo>(
    competenciaDoEndereco ?? '',
    () => servidor.repassesDoCampo(competenciaDoEndereco),
    aoSair,
    paraImprimir
  )
  const mes = dados?.competencia ?? competenciaDoEndereco ?? null
  const andar = (para: string): void => irPara(enderecoDaTelaDoCampo('repasses', para))

  return (
    <>
      {!paraImprimir && (
        <BarraDeCima eu={eu} aoSair={aoSair} voltarPara={{ endereco: enderecoDoCampo(mes), texto: 'Campo' }} />
      )}
      <main className="folha folha-lancamento tela-dos-repasses" data-pronto={dados !== null ? 'sim' : erro !== null ? 'erro' : 'nao'}>
        {erro !== null && <p className="recado" role="alert">{erro}</p>}
        {dados === null && erro === null && <p className="recado aguarde">Carregando...</p>}

        {dados !== null && (
          <>
            {!paraImprimir && <AtualizadoAs atualizacao={atualizacao} />}
            {mes !== null && !paraImprimir && <NavegarMes mes={mes} andar={andar} />}

            <h1>REPASSES DO CAMPO – {dados.competencia_por_extenso.toUpperCase()}</h1>

            <TabelaDeRepasses linhas={dados.linhas} vazio={`Nenhum repasse em ${dados.competencia_por_extenso}.`} />

            <h2>Resumo do mês, nas três direções</h2>
            <div className="rola-a-tabela">
              <table className="grade resumo-dos-repasses">
                <colgroup>
                  <col style={{ width: '34%' }} /><col style={{ width: '22%' }} /><col style={{ width: '22%' }} /><col style={{ width: '22%' }} />
                </colgroup>
                <thead>
                  <tr><th></th><th className="valor">AGUARDANDO APROVAÇÃO</th><th className="valor">APROVADO, A PAGAR</th><th className="valor">PAGO</th></tr>
                </thead>
                <tbody>
                  <LinhaDoResumo rotulo="Recebido das congregações" r={dados.resumo.recebido_das_congregacoes} />
                  <LinhaDoResumo rotulo="Enviado às congregações (ajuda)" r={dados.resumo.enviado_as_congregacoes} />
                  <LinhaDoResumo rotulo="Enviado à Igreja Matriz" r={dados.resumo.enviado_a_matriz} />
                </tbody>
              </table>
            </div>
            <p className="explicacao">
              Só a coluna PAGO é dinheiro que já mudou de mão, e conta o valor que de fato foi pago.
            </p>

            <h2>As regras em vigor em {dados.competencia_por_extenso}</h2>
            <table className="grade regras-em-vigor">
              <colgroup><col style={{ width: '34%' }} /><col /></colgroup>
              <thead><tr><th>CONGREGAÇÃO</th><th>REGRA</th></tr></thead>
              <tbody>
                {dados.regras.map((r) => (
                  <tr key={r.congregacao_id}>
                    <td className="impresso">
                      {alcanca(eu.alcance_das_telas.repasses, r.congregacao_id) ? (
                        <button className="como-link" onClick={() => irPara(enderecoDaTelaDaCongregacao('repasses', r.congregacao_id, mes))}>
                          {r.congregacao}
                        </button>
                      ) : r.congregacao}
                    </td>
                    <td>{aRegra(r.regra)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </main>
    </>
  )
}
